<template>
  <div v-if="canViewAccess" class="file-access-management w-full">
    <div class="flex justify-between items-center mb-[10px] mt-[34px] font-600">
      <div>
        {{ t('canvasUI.file.details.menu.access') }}
      </div>
      <CButton v-if="!isLoading" size="small" type="transparent" @click="toggleEdit">
        {{ isEditing ? t('canvasUI.common.done') : t('canvasUI.common.edit') }}
      </CButton>
    </div>

    <div v-if="isLoading" class="flex justify-center py-4">
      <CSpin />
    </div>

    <div v-else-if="isEditing" class="flex flex-col gap-4">
      <!-- Users Section -->
      <CUsersGroupsAccessManage
        v-if="allowUsersInDistribution"
        :items="usersCollaborations"
        itemType="user"
        :maxItemsToRender="10"
        :selectQuery="() => useFetchUserInstanceMembershipsByInstanceId(selectedInstance!.id)"
        :title="t('canvasUI.file.details.access.members', usersCollaborations.length)"
        @delete="onDeleteUser"
        @select="onSelectUser"
      />

      <!-- Groups Section -->
      <CUsersGroupsAccessManage
        :items="groupsCollaborations"
        itemType="group"
        :maxItemsToRender="10"
        :selectQuery="() => useFetchContentDistributionGroupsByInstanceId(selectedInstance!.id)"
        :title="t('canvasUI.file.details.access.distributionGroups', groupsCollaborations.length)"
        @delete="onDeleteGroup"
        @select="onSelectGroup"
      />
    </div>

    <div v-else class="flex flex-col gap-4">
      <CUsersAccessInfo
        v-if="allowUsersInDistribution && usersCollaborations.length > 0"
        :items="usersCollaborations"
        :title="t('canvasUI.file.details.access.members', usersCollaborations.length)"
      />
      <CGroupsAccessInfo
        v-if="groupsCollaborations.length > 0"
        :items="groupsCollaborations"
        :title="t('canvasUI.file.details.access.distributionGroups', groupsCollaborations.length)"
      />
      <div
        v-if="usersCollaborations.length === 0 && groupsCollaborations.length === 0"
        class="text-center py-4 text-gray-500"
      >
        {{ t('canvasUI.file.details.access.noAccess') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, inject, type ComputedRef, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import CSpin from '@lib/components/CSpin/CSpin.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CUsersAccessInfo from '@lib/components/CUsersAccessInfo/CUsersAccessInfo.vue'
import CGroupsAccessInfo from '@lib/components/CGroupsAccessInfo/CGroupsAccessInfo.vue'
import CUsersGroupsAccessManage from '@lib/components/CUsersGroupsAccessManage/CUsersGroupsAccessManage.vue'
import { CollaborationRoleEnum } from '@openapi'
import type { File, Collaboration } from '@openapi'
import type { PitcherEnv } from '@lib/sdk/interfaces'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { usePitcherApi } from '@lib/sdk/main'

const { t } = useI18n()
const api = usePitcherApi({ fetchMode: 'prefer-online' })

const props = defineProps<{
  file: File
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const isLoading = ref(false)
const isEditing = ref(false)
const collaborations = ref<Collaboration[]>([])
const env = ref<PitcherEnv | null>(null)
const selectedInstance = computed(() => env.value?.pitcher?.instance)

const allowUsersInDistribution = computed(() => !!launchDarkly.value?.enable_individual_users_in_file_distribution)
const allowRepFileDistribution = computed(() => !!launchDarkly.value?.allow_rep_file_distributions)
const isFileOwner = computed(() => {
  if (!env.value?.pitcher?.user?.id || !props.file?.owned_by?.id) return false
  return env.value.pitcher.user.id === props.file.owned_by.id
})
const canViewAccess = computed(() => allowRepFileDistribution.value && isFileOwner.value)

const usersCollaborations = computed(() => {
  return collaborations.value.filter((c: Collaboration) => !c.content_distribution_group)
})

const groupsCollaborations = computed(() => {
  return collaborations.value.filter((c: Collaboration) => !!c.content_distribution_group)
})

// Initialize env on mount
onMounted(async () => {
  try {
    env.value = await api.API.request('get_env')
  } catch (error) {
    console.error('[CFileAccessManagement] Error fetching env:', error)
  }
})

function toggleEdit() {
  isEditing.value = !isEditing.value
}

async function loadCollaborations() {
  if (!props.file?.id || !canViewAccess.value || !env.value) {
    collaborations.value = []
    return
  }

  isLoading.value = true
  try {
    const response: any = await api.API.request('get_collaborations', { file_id: props.file.id })
    collaborations.value = response.results || []
  } catch (error) {
    console.error('[CFileAccessManagement] Error fetching collaborations:', error)
    collaborations.value = []
  } finally {
    isLoading.value = false
  }
}

// Watch file.id and env to load collaborations
watch([() => props.file?.id, env], loadCollaborations, { immediate: true })

// Fetch functions for selectors
const useFetchUserInstanceMembershipsByInstanceId = (instanceId: string) => {
  const data = ref([])
  const isFetching = ref(false)

  const fetch = async () => {
    if (!env.value) return
    isFetching.value = true
    try {
      const response: any = await api.API.request('get_instance_memberships', {
        instance_id: instanceId,
        type: 'user',
      })
      data.value = response.results || []
    } catch (error) {
      console.error('Error fetching instance memberships:', error)
      data.value = []
    } finally {
      isFetching.value = false
    }
  }

  fetch()

  return { data, isFetching }
}

const useFetchContentDistributionGroupsByInstanceId = (instanceId: string) => {
  const data = ref([])
  const isFetching = ref(false)

  const fetch = async () => {
    if (!env.value) return
    isFetching.value = true
    try {
      const response: any = await api.API.request('get_content_distribution_groups', { instance_id: instanceId })
      data.value = response.results || []
    } catch (error) {
      console.error('Error fetching content distribution groups:', error)
      data.value = []
    } finally {
      isFetching.value = false
    }
  }

  fetch()

  return { data, isFetching }
}

async function onSelectUser(_scroller: any, selectItem: any) {
  if (!props.file?.id || !env.value) return
  try {
    await api.API.request('create_collaboration', {
      file_id: props.file.id,
      user_id: selectItem.user.id,
      role: CollaborationRoleEnum.VIEWER,
    })
    await loadCollaborations()
  } catch (e) {
    console.error('Failed to add user:', e)
  }
}

async function onSelectGroup(_scroller: any, selectItem: any) {
  if (!props.file?.id || !env.value) return
  try {
    await api.API.request('create_collaboration', {
      file_id: props.file.id,
      content_distribution_group_id: selectItem.id,
      role: CollaborationRoleEnum.VIEWER,
    })
    await loadCollaborations()
  } catch (e) {
    console.error('Failed to add group:', e)
  }
}

async function onDeleteUser(collaboration: any) {
  if (!props.file?.id || !collaboration.user || !env.value) return
  try {
    await api.API.request('delete_collaboration', { collaboration_id: collaboration.id })
    await loadCollaborations()
  } catch (e) {
    console.error('Failed to remove user:', e)
  }
}

async function onDeleteGroup(collaboration: any) {
  if (!props.file?.id || !collaboration.content_distribution_group || !env.value) return
  try {
    await api.API.request('delete_collaboration', { collaboration_id: collaboration.id })
    await loadCollaborations()
  } catch (e) {
    console.error('Failed to remove group:', e)
  }
}
</script>
